// src/services/billingService.js
// Production-grade thin client for Billing APIs (platform Stripe)
// Uses your axios instance (auth headers, baseURL, interceptors already configured)

import api from '../api/axios';

const BASE = '/billing';

// Utility: cheap idempotency key (avoids dependency on uuid)
const idem = (prefix = 'idem') =>
  `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

/* =============================================================================
   Canonical helpers
============================================================================= */

/**
 * Canonical Billing summary for Settings → Billing.
 * Server: GET /billing/summary
 * Shape (from backend):
 * {
 *   companyId,
 *   plan,
 *   billingStatus,
 *   billingCurrency,
 *   billingCountry,
 *   seatsPurchased,
 *   seatsInUse,
 *   trialEndsAt,
 *   defaultPaymentMethod: { brand, last4, expMonth, expYear } | null,
 *   stripe: { customerId, subscriptionId, subscriptionStatus, currentPeriodEnd },
 *   nextInvoice: { amountDue, currency, nextPaymentAttempt, dueDate } | null,
 *   health: {
 *     ok,
 *     level,          // 'ok' | 'warning' | 'error'
 *     checkedAt,
 *     reason,         // 'MISMATCH' | 'COMPANY_NOT_FOUND' | 'STRIPE_FETCH_FAILED' | null
 *     mismatches,     // [...details]
 *     db,             // snapshot of Company billing fields
 *     stripe          // snapshot of Stripe subscription
 *   } | null
 * }
 */
export async function getBillingSummary() {
  const { data } = await api.get(`${BASE}/summary`);
  return data;
}

/**
 * Back-compat alias used throughout the app.
 * Now returns the full billing summary instead of the old /billing/plan payload.
 */
export async function getPlan() {
  // Delegate to the canonical summary endpoint
  return getBillingSummary();
}

/**
 * Get high-level usage stats (for display only).
 * Server: GET /billing/usage
 */
export async function getUsage() {
  const { data } = await api.get(`${BASE}/usage`);
  return data;
}

/* =============================================================================
   Invoices
============================================================================= */

/**
 * Canonical invoices list for Billing tab.
 * Server: GET /billing/invoices?limit=12
 * Returns: { invoices: [...] }
 */
export async function getInvoices(params = {}) {
  const query = {};
  if (params.limit) query.limit = params.limit;

  const { data } = await api.get(`${BASE}/invoices`, {
    params: Object.keys(query).length ? query : undefined,
  });
  return data; // { invoices }
}

/**
 * Back-compat alias for older code that used getBillingInvoices().
 */
export async function getBillingInvoices(params = {}) {
  return getInvoices(params);
}

/* =============================================================================
   Health (Stripe ↔ DB sanity + webhook status)
============================================================================= */

/**
 * Billing health snapshot:
 * Server: GET /billing/health
 * Returns:
 * {
 *   ok: boolean,
 *   level: 'ok' | 'warning' | 'error',
 *   checkedAt: string,
 *   reason: string | null,
 *   mismatches: [...],
 *   db: { ... },
 *   stripe: { ... }
 * }
 */
export async function getBillingHealth() {
  const { data } = await api.get(`${BASE}/health`);
  return data;
}

/**
 * Force a resync between Stripe and DB when health is bad.
 * Server: POST /billing/health/resync
 * Body: {}
 * Returns the same shape as getBillingHealth(), plus optional details.
 */
export async function resyncBillingHealth(params = {}) {
  const headers = {
    'Idempotency-Key': params.idempotencyKey || idem('billing_health_resync'),
  };

  const { data } = await api.post(`${BASE}/health/resync`, {}, { headers });
  return data;
}

/* =============================================================================
   Mutations
============================================================================= */

/**
 * Adjust subscription seat count.
 * Server: POST /billing/adjust-seats
 * Body: { seats }
 */
export async function adjustSeats(seats, options = {}) {
  const payload = { seats };
  const headers = {
    'Idempotency-Key': options.idempotencyKey || idem('adjust_seats'),
  };

  const { data } = await api.post(`${BASE}/adjust-seats`, payload, { headers });
  return data;
}

/**
 * Start a Stripe Checkout session (subscription).
 * Server: POST /billing/checkout
 * Body: { plan, seats, currency, trialDays? }
 * Returns: { url }
 */
export async function createCheckout(params = {}) {
  const payload = {
    plan: params.plan, // 'starter' | 'pro' | 'advanced' (future)
    seats: params.seats,
    currency: params.currency, // 'CAD' | 'USD'
    trialDays: params.trialDays,
  };

  const headers = {
    'Idempotency-Key': params.idempotencyKey || idem('checkout'),
  };

  const { data } = await api.post(`${BASE}/checkout`, payload, { headers });
  if (!data?.url) throw new Error('Checkout URL missing from server response');
  return data; // { url }
}

/**
 * Back-compat alias for older code that used startCheckout().
 */
export async function startCheckout(params = {}) {
  return createCheckout(params);
}

/**
 * Open Stripe Customer Portal (manage subscription, payment method, invoices).
 * Server: POST /billing/portal
 * Returns: { url }
 */
export async function openPortal(params = {}) {
  const payload = {
    returnUrl: params.returnUrl, // backend currently ignores; safe to pass
  };

  const headers = {
    'Idempotency-Key': params.idempotencyKey || idem('portal'),
  };

  const { data } = await api.post(`${BASE}/portal`, payload, { headers });
  if (!data?.url) throw new Error('Portal URL missing from server response');
  return data; // { url }
}

/* =============================================================================
   Status helpers (used by Topbar, BillingTab, TeamManagement, etc.)
============================================================================= */

/**
 * Returns true when subscription should be treated as **read-only** on FE:
 * - canceled
 * - unpaid
 * - incomplete
 * - incomplete_expired
 */
export function isSubscriptionReadOnly(billingStatus) {
  const s = String(billingStatus || '').toLowerCase();
  return ['canceled', 'unpaid', 'incomplete', 'incomplete_expired'].includes(s);
}

/**
 * Returns true when subscription is in a **warning** state, but not yet locked.
 * - past_due
 */
export function isSubscriptionWarning(billingStatus) {
  const s = String(billingStatus || '').toLowerCase();
  return s === 'past_due';
}
